/**
 * The company-wide priority distribution — one row of priority tiles carrying their urgency split,
 * and the role list they filter.
 *
 * Moved here from Cluster Lead Check-In (Theresa, Sep 25 2026). The Company View is the page
 * business stakeholders open to find out how THEIR open roles are rated and what TA has capacity
 * for, and the answer to the first half of that was on a tab built for the cluster leads' weekly.
 *
 * ⚠️ THE THREE MOVE TOGETHER, and that is not tidiness. Clicking a matrix cell filters the table
 * below it; splitting them across two tabs would leave a control on one page acting on a list that
 * is not there. The selection state therefore lives here, with them.
 *
 * ⚠️ IT READS A DIFFERENT SOURCE FROM THE REST OF THIS TAB, and the footer says so. Everything
 * above comes from Airtable `hiring_plan`; this comes from the TA Cockpit Google Sheet, because
 * **urgency exists only there** — it is a column somebody types on the CL Check-In tab and there is
 * no Airtable field for it. A priority × urgency matrix built from the Airtable rows would be a
 * matrix with one axis missing, so the sheet read stays until urgency moves into the HDB.
 *
 * ⚠️ IT FETCHES AND DEGRADES ON ITS OWN. A sheet outage renders one line inside this section and
 * leaves the score above it working — that score reads Airtable and has no reason to go down with
 * the sheet. Same rule as the Pipeline Radar's candidate funnel.
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { fetchCheckInData } from '../../../services/taCheckinApi';
import type { CheckInData, CheckInRole } from '../../../types/taCheckin';
import { normaliseCheckInRoles } from '../../../utils/taCheckinIndex';
import { PriorityUrgencyTiles, type MatrixSelection } from '../checkin/PriorityMatrix';
import { RoleTable } from '../checkin/RoleTable';
import { Card, SectionHead } from './prioritisationUi';

export function PriorityDistribution() {
  const [data, setData] = useState<CheckInData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activeTier, setActiveTier] = useState<string | null>(null);
  const [matrixSel, setMatrixSel] = useState<MatrixSelection | null>(null);
  const [matrixFilterIds, setMatrixFilterIds] = useState<Set<string> | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchCheckInData()
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setError(null);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ⚠️ The SAME normalisation the Check-In tab applied before these three moved here: an unrated
  // role needs a tier to sit in and the matrix buckets on a lower-cased urgency string.
  const roles: CheckInRole[] = useMemo(() => normaliseCheckInRoles(data?.roles ?? []), [data]);

  /** Picking a cell clears the table filter it produced — the old ids belonged to the old cell. */
  function selectMatrix(next: MatrixSelection | null) {
    setMatrixSel(next);
    setMatrixFilterIds(null);
  }

  function toggleTableFilter(selected: CheckInRole[]) {
    setMatrixFilterIds((prev) => (prev ? null : new Set(selected.map((r) => r.positionId || r.title))));
  }

  return (
    <section>
      <SectionHead
        number="04"
        title="Every open role, by priority"
        aside={data ? `${roles.length} roles · click a tile or chip to filter` : undefined}
      />

      {loading ? (
        <Card className="flex items-center gap-2 p-5 text-sm text-iron">
          <Loader2 className="h-4 w-4 animate-spin" />
          Reading the roles…
        </Card>
      ) : error || !data ? (
        <Card className="flex items-start gap-2 p-5 text-sm text-iron">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-orange" />
          <span>
            The role list could not be read{error ? `: ${error}` : ''}. The figures above are
            unaffected — they come from a different source.
          </span>
        </Card>
      ) : (
        <div className="space-y-4">
          <PriorityUrgencyTiles
            roles={roles}
            activeTier={activeTier}
            onSelectTier={(t) => {
              setActiveTier(t);
              selectMatrix(null);
            }}
            selection={matrixSel}
            onSelect={selectMatrix}
            tableFiltered={matrixFilterIds != null}
            onToggleTableFilter={toggleTableFilter}
          />

          <RoleTable
            roles={roles}
            activeTier={activeTier}
            matrixFilterIds={matrixFilterIds}
            onClearExternalFilters={() => {
              setActiveTier(null);
              selectMatrix(null);
            }}
          />

          <p className="text-[11px] leading-4 text-steel">
            Roles and urgency come from the TA Cockpit sheet, which is the only place urgency is
            recorded — so this count can differ from the Airtable figure above.
          </p>
        </div>
      )}
    </section>
  );
}
